import { useEffect, useState, type FormEvent } from 'react';
import { Button, Card, CardTitle, Checkbox, FormField, Input, Select } from '@invested/ui';
import * as api from '../../services/api';
import { ApiError } from '../../services/api';
import type { Participant, SampleRole } from '../../types';

// SOP-02: role of the person who took/double-checked a sample — a fixed
// set, never a free-text name (see backend/src/services/schemas.ts).
const SAMPLE_ROLE_OPTIONS: { value: SampleRole; label: string }[] = [
  { value: 'LEGE', label: 'Lege' },
  { value: 'SYKEPLEIER', label: 'Sykepleier' },
  { value: 'BIOINGENIOR', label: 'Bioingeniør' },
  { value: 'ANNET', label: 'Annet' },
];

/**
 * Fast intake flow for draw day (SOP-02): scan/type the participant and
 * cryo label, then confirm the two-person double-check before submitting.
 * The submit button stays disabled until the check is confirmed.
 */
export function NewSamplePage() {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [participantId, setParticipantId] = useState('');
  const [type, setType] = useState('VENOUS_EDTA');
  const [cryoLabelId, setCryoLabelId] = useState('');
  const [takenByRole, setTakenByRole] = useState<SampleRole | ''>('');
  const [freezerBox, setFreezerBox] = useState('');
  const [freezerRow, setFreezerRow] = useState('');
  const [freezerColumn, setFreezerColumn] = useState('');
  const [doubleCheckConfirmed, setDoubleCheckConfirmed] = useState(false);
  const [doubleCheckByRole, setDoubleCheckByRole] = useState<SampleRole | ''>('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    api.getParticipants().then(setParticipants).catch(() => undefined);
  }, []);

  function resetForFastEntry() {
    setCryoLabelId('');
    setDoubleCheckConfirmed(false);
    setDoubleCheckByRole('');
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!doubleCheckConfirmed || !takenByRole) return;
    setPending(true);
    setError(null);
    setSuccess(null);
    try {
      const sample = await api.createSample({
        participantId,
        type,
        cryoLabelId: cryoLabelId.trim(),
        takenByRole,
        freezerBox: freezerBox || undefined,
        freezerRow: freezerRow || undefined,
        freezerColumn: freezerColumn || undefined,
        doubleCheckConfirmed: true,
        doubleCheckByRole: doubleCheckByRole || undefined,
      });
      setSuccess(`Prøve ${sample.cryoLabelId} registrert.`);
      resetForFastEntry();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Kunne ikke registrere prøven.');
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <CardTitle as="h1">Prøveregistrering</CardTitle>
        <p className="mt-1 text-sm text-black/55">
          Rask flyt for prøvetakingsdagen. Kryo-etiketten må dobbeltkontrolleres av to personer før registrering (SOP-02).
        </p>
      </div>
      <Card size="lg">
        <form onSubmit={submit} className="space-y-5">
          <FormField label="Deltaker" htmlFor="participantId" required>
            <Select
              id="participantId"
              required
              value={participantId}
              onChange={(e) => setParticipantId(e.target.value)}
              placeholder="Velg deltaker…"
              options={participants.map((p) => ({ value: p.id, label: p.pseudonymId }))}
            />
          </FormField>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Prøvetype" htmlFor="type" required>
              <Select
                id="type"
                required
                value={type}
                onChange={(e) => setType(e.target.value)}
                options={[
                  { value: 'VENOUS_EDTA', label: 'Venøs (EDTA)' },
                  { value: 'DBS_CARD', label: 'DBS-kort' },
                ]}
              />
            </FormField>
            <FormField label="Kryo-ID (skann eller skriv)" htmlFor="cryoLabelId" required>
              <Input
                id="cryoLabelId"
                required
                autoFocus
                value={cryoLabelId}
                onChange={(e) => setCryoLabelId(e.target.value)}
              />
            </FormField>
          </div>

          <FormField label="Rolle til den som tok prøven (ikke navn)" htmlFor="takenByRole" required>
            <Select
              id="takenByRole"
              required
              value={takenByRole}
              onChange={(e) => setTakenByRole(e.target.value as SampleRole)}
              placeholder="Velg rolle…"
              options={SAMPLE_ROLE_OPTIONS}
            />
          </FormField>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <FormField label="Fryserboks" htmlFor="freezerBox" optional>
              <Input id="freezerBox" value={freezerBox} onChange={(e) => setFreezerBox(e.target.value)} />
            </FormField>
            <FormField label="Rad" htmlFor="freezerRow" optional>
              <Input id="freezerRow" value={freezerRow} onChange={(e) => setFreezerRow(e.target.value)} />
            </FormField>
            <FormField label="Kolonne" htmlFor="freezerColumn" optional>
              <Input id="freezerColumn" value={freezerColumn} onChange={(e) => setFreezerColumn(e.target.value)} />
            </FormField>
          </div>

          <div className="rounded-xl border border-amber-300 bg-amber-50 p-4">
            <Checkbox
              label="Dobbeltkontroll bekreftet — to personer har verifisert at kryo-ID stemmer med deltakeren (SOP-02)"
              checked={doubleCheckConfirmed}
              onChange={(e) => setDoubleCheckConfirmed(e.target.checked)}
            />
            <div className="mt-3">
              <FormField label="Rolle til kontrollør (valgfritt)" htmlFor="doubleCheckByRole" optional>
                <Select
                  id="doubleCheckByRole"
                  value={doubleCheckByRole}
                  onChange={(e) => setDoubleCheckByRole(e.target.value as SampleRole)}
                  placeholder="Velg rolle…"
                  options={SAMPLE_ROLE_OPTIONS}
                />
              </FormField>
            </div>
          </div>

          {error && (
            <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
              {error}
            </p>
          )}
          {success && (
            <p className="rounded-xl bg-green-50 px-4 py-3 text-sm text-green-800" role="status">
              {success}
            </p>
          )}

          <Button type="submit" disabled={pending || !doubleCheckConfirmed || !takenByRole}>
            {pending ? 'Registrerer…' : 'Registrer prøve'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
